import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {isPlainObject} from 'lodash';

import {Box, useMultiStyleConfig} from '@amblique/base/app/components/shared/ui';
import {Component} from '@amblique/king-living/app/components/contentful/component';
import {Swiper} from '@amblique/king-living/app/components/shared/swiper';
import {ShopTheLookEmbedProvider} from '@amblique/king-living/app/contentful/models/shop-the-look-embed-context';
import {withMediaCarouselVideoPreset} from '@amblique/king-living/app/utils/media-carousel-video-preset';

const GAP_MOBILE_PX = 40;
const GAP_DESKTOP_PX = 48;
const DESKTOP_BREAKPOINT_PX = 992;
const APPEARANCE_CF_012 = 'CF-012';

/** When Appearance is unset in Contentful, use a defined Swiper variant so slides are visible. */
const DEFAULT_MEDIA_CAROUSEL_APPEARANCE = 'CF-012';

/** Default delay (ms) when Contentful "Auto Play" is on; aligns with CF-010 / Swiper module defaults. */
const AUTOPLAY_DEFAULT_DELAY_MS = 5000;

const APPEARANCE_CF_046 = 'CF-046';

/**
 * CF-046 only: CMS slide count → React-pad target (weak Swiper loop + auto widths).
 * 1 slide: force loop off (no pad). Missing key = no pad.
 */
const CF046_LOOP_PAD_TARGET_BY_COUNT = {
  2: 6, // A B ×3
  3: 6, // A B C ×2
  4: 8, // A…D ×2
  5: 10, // A…E ×2
};

/**
 * @param {number} cmsCount
 * @returns {number|null}
 */
function getCf046LoopPadTarget(cmsCount) {
  return CF046_LOOP_PAD_TARGET_BY_COUNT[cmsCount] ?? null;
}

/**
 * @param {Array<{sys?: {id?: string}}>} slides
 * @returns {Array<{item: object, key: string}>}
 */
function buildCf046LoopSlideEntries(slides) {
  const list = Array.isArray(slides) ? slides : [];
  const target = getCf046LoopPadTarget(list.length);

  if (!target) {
    return list.map((item, i) => ({
      item,
      key: item?.sys?.id || `slide-${i}`,
    }));
  }

  const entries = [];
  let copy = 0;

  while (entries.length < target) {
    for (let i = 0; i < list.length && entries.length < target; i += 1) {
      const item = list[i];
      const baseId = item?.sys?.id || `idx-${i}`;

      entries.push({
        item,
        key: `${baseId}__cf046-pad-${copy}-${i}`,
      });
    }
    copy += 1;
  }

  return entries;
}

/**
 * @param {unknown} raw - Contentful JSON field (object or stringified JSON)
 * @returns {Record<string, unknown>}
 */
function normalizeContentfulOptions(raw) {
  if (raw == null) {
    return {};
  }

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);

      return isPlainObject(parsed) ? {...parsed} : {};
    } catch {
      return {};
    }
  }

  return isPlainObject(raw) ? {...raw} : {};
}

/**
 * Apply Contentful Media Carousel `autoPlay` boolean to Swiper options (main carousel only).
 *
 * @param {Record<string, unknown>} base - Normalized CMS `options` JSON
 * @param {boolean|undefined|null} autoPlay - Contentful "Auto Play" field
 * @returns {Record<string, unknown>}
 */
function mergeAutoPlayIntoSwiperOptions(base, autoPlay) {
  if (autoPlay === true) {
    const fromCms = isPlainObject(base.autoplay) ? base.autoplay : {};

    return {
      ...base,
      autoplay: {
        delay: AUTOPLAY_DEFAULT_DELAY_MS,
        pauseOnMouseEnter: true,
        disableOnInteraction: false,
        ...fromCms,
        enabled: true,
      },
    };
  }

  if (autoPlay === false) {
    return {
      ...base,
      autoplay: false,
    };
  }

  return base;
}

function getThumbsSwiperEl(rootEl) {
  if (!rootEl) return null;

  const swipers = rootEl.querySelectorAll('.swiper');

  return Array.from(swipers).find((el) => el.className.includes('-thumbs')) ?? null;
}

function getMainSwiperEl(rootEl) {
  if (!rootEl) return null;

  const swipers = rootEl.querySelectorAll('.swiper');

  return Array.from(swipers).find((el) => !el.className.includes('-thumbs')) ?? null;
}

function getActiveHeadingEl(rootEl) {
  const mainEl = getMainSwiperEl(rootEl);

  if (!mainEl) return null;

  const activeHeading = mainEl.querySelector('.swiper-slide-active [data-carousel-heading]');

  return activeHeading ?? mainEl.querySelector('[data-carousel-heading]') ?? null;
}

/**
 * MediaCarousel component
 * Override this component in your package to provide custom implementation.
 *
 * @param {Object} props - Component props
 * @param {Contentful.MediaCarousel} props.component - MediaCarousel component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement}
 */
export const MediaCarousel = ({component, isComplete = true, isFetching = false, ...props}) => {
  if (!component?.sys?.id) {
    return null;
  }

  const {appearance: appearanceRaw, autoPlay, presets, options, thumbnailsCollection, imagesCollection} = component;
  const appearance = appearanceRaw || DEFAULT_MEDIA_CAROUSEL_APPEARANCE;
  const [thumbnailsSwiper, setThumbnailsSwiper] = useState(null);
  const mainSwiperRef = useRef(null);
  const rootRef = useRef(null);
  const styles = useMultiStyleConfig('Swiper', {variant: appearance});

  const updateThumbsTopFromActiveHeading = useCallback(() => {
    if (appearance !== APPEARANCE_CF_012) return;

    const rootEl = rootRef.current;

    if (!rootEl) return;

    const thumbsEl = getThumbsSwiperEl(rootEl);
    const headingEl = getActiveHeadingEl(rootEl);

    if (!thumbsEl || !headingEl) return;

    const rootRect = rootEl.getBoundingClientRect();
    const headingRect = headingEl.getBoundingClientRect();
    const headingTop = headingRect.top - rootRect.top;
    const headingHeight = headingRect.height;
    const isDesktop = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT_PX}px)`).matches;
    const gapPx = isDesktop ? GAP_DESKTOP_PX : GAP_MOBILE_PX;
    const topPx = headingTop + headingHeight + gapPx;

    thumbsEl.style.top = `${topPx}px`;
  }, [appearance]);

  const runUpdateAfterDomReady = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(updateThumbsTopFromActiveHeading);
    });
  }, [updateThumbsTopFromActiveHeading]);

  const onMainSwiper = useCallback(
    (swiper) => {
      mainSwiperRef.current = swiper;

      if (swiper && appearance === APPEARANCE_CF_012) {
        runUpdateAfterDomReady();
        swiper.on('slideChange', runUpdateAfterDomReady);
      }
    },
    [appearance, runUpdateAfterDomReady]
  );

  const handleThumbActivate = useCallback((index) => {
    const swiper = mainSwiperRef.current;

    if (!swiper || swiper.realIndex === index) {
      return;
    }

    // Preset may enable loop (KL-1103) — use slideToLoop for the real index.
    if (swiper.params?.loop && typeof swiper.slideToLoop === 'function') {
      swiper.slideToLoop(index);
    } else {
      swiper.slideTo(index);
    }
  }, []);

  useEffect(() => {
    if (appearance !== APPEARANCE_CF_012) return undefined;

    let timeoutId = null;
    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        runUpdateAfterDomReady();
        timeoutId = null;
      }, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [appearance, runUpdateAfterDomReady]);

  useEffect(() => {
    return () => {
      if (mainSwiperRef.current) {
        mainSwiperRef.current.off('slideChange');
        mainSwiperRef.current = null;
      }
    };
  }, []);

  const thumbsOptions = useMemo(() => ({
    swiper: thumbnailsSwiper,
  }), [thumbnailsSwiper]);

  const thumbnails = useMemo(
    () => (thumbnailsCollection?.items ?? []).filter((item) => item?.sys?.id),
    [thumbnailsCollection?.items]
  );
  const images = useMemo(
    () => (imagesCollection?.items ?? []).filter((item) => item?.sys?.id),
    [imagesCollection?.items]
  );

  const slides = useMemo(
    () => images.map((item) => withMediaCarouselVideoPreset(item)),
    [images]
  );

  const cmsOptions = useMemo(() => normalizeContentfulOptions(options), [options]);

  // CMS may send boolean false or string "false".
  const cmsLoopDisabled =
    cmsOptions.loop === false || cmsOptions.loop === 'false';

  // CF-046: 1 slide cannot loop — force off even if preset/CMS say loop: true.
  const cf046ForceNoLoop =
    appearance === APPEARANCE_CF_046 && (cmsLoopDisabled || slides.length === 1);

  // Loop comes from each CF-* preset in swiper-options (not a shared default).
  // CMS may opt out with options: { "loop": false }.
  const mainSwiperOptions = useMemo(() => {
    const merged = mergeAutoPlayIntoSwiperOptions(cmsOptions, autoPlay);

    if (cf046ForceNoLoop) {
      return {...merged, loop: false};
    }

    return merged;
  }, [appearance, autoPlay, cf046ForceNoLoop, cmsOptions]);

  // CF-046 only (loop on): React-pad 2→6, 3→6, 4→8, 5→10. Never pad when loop forced off.
  const mainSlideEntries = useMemo(() => {
    const useCf046Pad =
      appearance === APPEARANCE_CF_046 &&
      !cf046ForceNoLoop &&
      getCf046LoopPadTarget(slides.length) != null;

    if (useCf046Pad) {
      return buildCf046LoopSlideEntries(slides);
    }

    return slides.map((item, i) => ({
      item,
      key: item?.sys?.id || `slide-${i}`,
    }));
  }, [appearance, cf046ForceNoLoop, slides]);

  const thumbsStripOptions = useMemo(() => {
    const {loop: _ignoredLoop, ...rest} = cmsOptions;

    return {
      ...rest,
      loop: false,
      autoplay: false,
    };
  }, [cmsOptions]);

  return isComplete ? (
    <ShopTheLookEmbedProvider>
      <Box
        ref={rootRef}
        data-kl-media-carousel=""
        {...styles.root}
        {...props}
      >
        {thumbnails.length > 0 && (
          <Swiper
            options={thumbsStripOptions}
            presets={`${appearance ? `${appearance}-Thumbs` : ''}, ${presets ? `${presets}-Thumbs` : ''}`}
            variant={appearance ? `${appearance}-Thumbs` : ''}
            onSwiper={setThumbnailsSwiper}
            onThumbActivate={handleThumbActivate}
            thumbActivateOnClick={false}
          >
            {thumbnails.map((thumbnail) => (
              <Component key={thumbnail.sys.id} component={thumbnail} />
            ))}
          </Swiper>
        )}

        <Swiper
          options={mainSwiperOptions}
          presets={`${appearance || ''}, ${presets || ''}`}
          // CF-046 edge snap only when author disables loop via CMS.
          snapLastSlide={appearance === APPEARANCE_CF_046 && cf046ForceNoLoop}
          thumbs={thumbsOptions}
          variant={appearance}
          onSwiper={onMainSwiper}
        >
          {mainSlideEntries.map(({item, key}) => (
            <Component key={key} component={item} />
          ))}
        </Swiper>
      </Box>
    </ShopTheLookEmbedProvider>
  ) : null;
};

MediaCarousel.displayName = 'MediaCarousel';
MediaCarousel.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
